import type { InterfaceFile } from "~/classes/file/InterfaceFile"

interface State {
  visible: boolean
  target?: ContextMenuTarget
  type?: ContextMenuType
  x: number
  y: number
}
let contextMenuState: Ref<State>
function defaultState(): State {
  return {
    visible: false,
    x: 0,
    y: 0,
  }
}
function clearContextMenuState() {
  contextMenuState.value = defaultState()
}

function isContextMenuVisible() {
  return contextMenuState.value.visible
}

function showContextMenu(
  mouse: MouseEvent,
  type: ContextMenuType,
  target?: ContextMenuTarget,
  xPos?: number,
  yPos?: number,
) {
  const { capture } = useAnalytics()
  contextMenuState.value = {
    target,
    type,
    visible: true,
    x: xPos ?? mouse.pageX,
    y: yPos ?? mouse.pageY,
  }
  capture("user_opened_context_menu")
}

function hideContextMenu() {
  contextMenuState.value.visible = false
  contextMenuState.value.type = undefined
}

function _contextMenuTargetSelection(): Record<string, InterfaceFile> | undefined {
  const { selectedFiles } = useSelection()
  const { target } = contextMenuState.value
  if (target && selectedFiles.value[target.fileId])
    return selectedFiles.value
  return undefined
}

/**
 * The context menu target is the selection if the context menu target is in the selection, otherwise undefined
 */
const contextMenuTargetSelection = computed(() => _contextMenuTargetSelection())

function uploadHere(folder: InterfaceFile) {
  if (isDesktop()) {
    const { uploadFromDesktopFilePicker } = useTauri()
    return uploadFromDesktopFilePicker(folder)
  }
  else {
    const { uploadFromFilePicker } = useCache()
    return uploadFromFilePicker(folder)
  }
}

function moveToArchive(files: Record<string, InterfaceFile>) {
  const { moveFiles } = useFileSystem()
  const { state: myself } = useSelf()
  const { selectedFiles, deselectFile } = useSelection()
  const toMove = selectedFiles.value

  if (!myself.value.archive)
    return console.error("Anon users can't move files")
  if (!len(files))
    return console.error("Empty list of files to move")

  // if no files are selected, use the context menu target
  if (!len(toMove)) {
    const file = contextMenuState.value.target
    if (!file)
      throw new Error("Move to archive has no target")
    toMove[file.fileId] = file
  }

  for (const file of Object.values(files))
    deselectFile(file.fileId)

  moveFiles(files, myself.value.archive)
  return hideContextMenu()
}

function permanentlyDelete(files: Record<string, InterfaceFile>) {
  const { permanentlyDeleteFiles } = useFileSystem()
  const { deselectFile } = useSelection()
  for (const file of Object.values(files))
    deselectFile(file.fileId)

  return permanentlyDeleteFiles(files)
}

export default function () {
  contextMenuState = useState("context-menu-state", defaultState)

  return {
    contextMenuState,
    clearContextMenuState,
    isContextMenuVisible,
    showContextMenu,
    hideContextMenu,
    uploadHere,
    moveToArchive,
    permanentlyDelete,
    contextMenuTargetSelection,
  }
}
